import { track } from "@amplitude/analytics-react-native";
import { useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { useGatheringStore } from "src/contexts/GatheringContext";
import { getClientId } from "src/helpers/globalHelpers";
import useClientProfile from "src/hooks/useClientProfile";
import useGatheringReactionAsset from "src/hooks/useGatheringReactionAsset";
import { useModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import { PhotoItem } from "src/screens/(internal)/PhotoDetailModal/types";
import { paddingMedium } from "src/styles/spacing";
import usePhotoDetailStore from "../../stores/usePhotoDetailStore";
import ReactionButton from "./components/ReactionButton";
import ReactionPreviewProfilePictures from "./components/ReactionPreviewProfilePictures";

const PhotoDetailReactionsButton = ({
	photo,
	containerStyle,
	maxNumProfilePictures = 3,
}: {
	photo: PhotoItem;
	containerStyle?: StyleProp<ViewStyle>;
	/**
	 * @default 3
	 */
	maxNumProfilePictures?: number;
}) => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const { reactionAsset } = useGatheringReactionAsset(gatheringId);
	const asyncPhoto = usePhotoDetailStore((state) => state.asyncSettledPhoto);
	const { presentModal } = useModal<GatheringModal>();
	const { profile } = useClientProfile();
	const users = useMemo(() => {
		const clientId = getClientId();
		const users = (asyncPhoto?.reactionUsers?.payload ?? [])
			.map((user) => user.user)
			.filter(
				// Client profile handled separately
				(user) => user?.userId !== clientId,
			);
		if (asyncPhoto?.callerReaction?.didPrimaryReact && profile) {
			users.unshift({
				userId: profile.userId,
				profilePicture: profile.profilePicture,
			});
		}
		return users;
	}, [asyncPhoto, profile]);

	if (
		!reactionAsset ||
		asyncPhoto?.callerReaction == null ||
		asyncPhoto.photoId !== photo.photoId
	) {
		return null;
	}
	return (
		<View
			style={[
				{
					flexDirection: "row",
					alignItems: "center",
					columnGap: paddingMedium,
				},
				containerStyle,
			]}
		>
			<ReactionPreviewProfilePictures
				users={users}
				numProfilePictures={maxNumProfilePictures}
				onPress={() => {
					track("photoReactionModalViewClick");
					presentModal("PhotoReactionModal", {
						props: {
							photoId: photo.photoId,
							gatheringId,
							numPrimaryReaction: photo.numPrimaryReaction ?? undefined,
						},
					});
				}}
				size={32}
			/>
			<ReactionButton
				isSelected={asyncPhoto.callerReaction.didPrimaryReact ?? false}
				photo={photo}
				gatheringId={gatheringId}
				reactionAsset={reactionAsset}
			/>
		</View>
	);
};
export default PhotoDetailReactionsButton;
